import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Trash2,
  Edit,
  Image as ImageIcon,
  Plus,
  Save,
  Upload,
  X
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../Shared/Navbar/Navbar";
import { baseurl } from "../../Api/Baseurl";

const API_URL = `${baseurl}/api`;

const AdminCarousel = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState("");
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();

  const loadImages = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/carousel-images/admin`);
      if (res.data?.success) setImages(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  const toggleActive = async (id, status) => {
    try {
      await axios.put(`${API_URL}/carousel-images/${id}`, { is_active: !status });
      loadImages();
    } catch (err) {
      console.error("Error toggling status:", err);
      alert("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API_URL}/carousel-images/${id}`);
      loadImages();
    } catch (err) {
      console.error("Error deleting image:", err);
      alert("Failed to delete image");
    }
  };

  const moveImage = async (index, dir) => {
    if ((dir === "up" && index === 0) || (dir === "down" && index === images.length - 1)) return;

    const updated = [...images];
    const swapIndex = dir === "up" ? index - 1 : index + 1;
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];

    const payload = updated.map((img, i) => ({ id: img.id, display_order: i }));

    try {
      await axios.put(`${API_URL}/carousel-images/update-order`, { images: payload });
      setImages(updated);
    } catch (err) {
      console.error("Error updating order:", err);
      alert("Failed to update order");
    }
  };

  const handleEditClick = (image) => {
    setEditingId(image.id);
    setEditForm({ title: image.title || "", description: image.description || "" });
    setEditFile(null);
    setEditPreview("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: "", description: "" });
    setEditFile(null);
    setEditPreview("");
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditFile(file);
    setEditPreview(URL.createObjectURL(file));
  };

  const handleEditSubmit = async (imageId) => {
    try {
      setUpdating(true);
      const formData = new FormData();
      formData.append("title", editForm.title);
      formData.append("description", editForm.description);
      if (editFile) formData.append("image", editFile);

      await axios.put(`${API_URL}/carousel-images/${imageId}/update-with-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      handleCancelEdit();
      loadImages();
    } catch (err) {
      console.error("Error updating image:", err);
      alert("Failed to update image");
    } finally {
      setUpdating(false);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return "https://via.placeholder.com/400x220?text=No+Image";
    if (imageUrl.startsWith("http")) return imageUrl;
    return `${baseurl}${imageUrl}`;
  };

  return (
    <AdminLayout>
      <div className="ac-page">
        <div className="ac-header">
          <div>
            <h1 className="ac-title">Carousel Images</h1>
            <p className="ac-subtitle">
              {images.length} image{images.length !== 1 ? "s" : ""} · drag order with the arrows below
            </p>
          </div>
          <button onClick={() => navigate("/admin/carousel/upload")} className="ac-btn ac-btn-primary">
            <Plus size={18} />
            Upload Image
          </button>
        </div>

        {loading ? (
          <div className="ac-state-box">
            <div className="ac-spinner" />
            <p>Loading images…</p>
          </div>
        ) : images.length === 0 ? (
          <div className="ac-state-box">
            <ImageIcon className="ac-state-icon" size={40} />
            <p>No images found</p>
          </div>
        ) : (
          <div className="ac-grid">
            {images.map((img, index) => (
              <div key={img.id} className="ac-card">
                <div className="ac-card-image">
                  <img
                    src={editingId === img.id && editPreview ? editPreview : getImageUrl(img.image_url)}
                    alt={img.title || "Carousel image"}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/400x220?text=Image+Not+Found";
                    }}
                  />
                  <span className={`ac-status-badge ${img.is_active ? "is-active" : "is-inactive"}`}>
                    {img.is_active ? "Active" : "Hidden"}
                  </span>
                </div>

                <div className="ac-card-body">
                  {editingId === img.id ? (
                    <div className="ac-edit-form">
                      <div>
                        <label className="ac-label">Replace Image (optional)</label>
                        <div className="ac-dropzone ac-dropzone-sm">
                          <input type="file" id={`file-${img.id}`} hidden onChange={handleFileSelect} accept="image/*" />
                          <label htmlFor={`file-${img.id}`} className="ac-dropzone-label">
                            <Upload size={20} />
                            <span>Click to select new image</span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="ac-label">Title</label>
                        <input
                          type="text"
                          value={editForm.title}
                          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                          className="ac-input"
                          placeholder="Enter title"
                        />
                      </div>

                      <div>
                        <label className="ac-label">Description</label>
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="ac-input ac-textarea"
                          rows="2"
                          placeholder="Enter description"
                        />
                      </div>

                      <div className="ac-edit-actions">
                        <button onClick={() => handleEditSubmit(img.id)} disabled={updating} className="ac-btn ac-btn-success">
                          <Save size={14} />
                          {updating ? "Saving…" : "Save"}
                        </button>
                        <button onClick={handleCancelEdit} className="ac-btn ac-btn-neutral">
                          <X size={14} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="ac-card-meta">
                        <p className="ac-order">Order #{img.display_order + 1}</p>
                        <p className="ac-date">
                          {new Date(img.created_at).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric"
                          })}
                        </p>
                        {img.title && <p className="ac-item-title">{img.title}</p>}
                        {img.description && <p className="ac-item-desc">{img.description}</p>}
                      </div>

                      <div className="ac-card-actions">
                        <div className="ac-move-group">
                          <button onClick={() => moveImage(index, "up")} disabled={index === 0} className="ac-icon-btn" title="Move up">
                            <ArrowUp size={16} />
                          </button>
                          <button
                            onClick={() => moveImage(index, "down")}
                            disabled={index === images.length - 1}
                            className="ac-icon-btn"
                            title="Move down"
                          >
                            <ArrowDown size={16} />
                          </button>
                        </div>

                        <div className="ac-action-group">
                          <button
                            onClick={() => toggleActive(img.id, img.is_active)}
                            className={`ac-icon-btn ac-toggle ${img.is_active ? "is-on" : "is-off"}`}
                            title={img.is_active ? "Active" : "Inactive"}
                          >
                            {img.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                          <button onClick={() => handleEditClick(img)} className="ac-icon-btn ac-icon-edit" title="Edit">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDelete(img.id)} className="ac-icon-btn ac-icon-delete" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .ac-page { padding: 32px; background: #f4f5f7; min-height: 100vh; box-sizing: border-box; }
        .ac-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 28px; gap: 16px; flex-wrap: wrap; }
        .ac-title { font-size: 26px; font-weight: 700; color: #111827; margin: 0; }
        .ac-subtitle { font-size: 14px; color: #6b7280; margin: 4px 0 0; }

        .ac-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 10px; font-weight: 600; font-size: 14px; border: none; cursor: pointer; transition: all 0.15s ease; }
        .ac-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .ac-btn-primary { background: #2563eb; color: #ffffff; box-shadow: 0 1px 2px rgba(0,0,0,0.08); }
        .ac-btn-primary:hover { background: #1d4ed8; }
        .ac-btn-success { background: #16a34a; color: #ffffff; flex: 1; justify-content: center; }
        .ac-btn-success:hover { background: #15803d; }
        .ac-btn-neutral { background: #e5e7eb; color: #374151; flex: 1; justify-content: center; }
        .ac-btn-neutral:hover { background: #d1d5db; }

        .ac-state-box { text-align: center; padding: 80px 20px; background: #ffffff; border-radius: 14px; border: 1px solid #e5e7eb; color: #6b7280; }
        .ac-state-icon { color: #9ca3af; margin: 0 auto 12px; }
        .ac-spinner { width: 28px; height: 28px; margin: 0 auto 14px; border: 3px solid #e5e7eb; border-top-color: #2563eb; border-radius: 50%; animation: ac-spin 0.7s linear infinite; }
        @keyframes ac-spin { to { transform: rotate(360deg); } }

        .ac-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 22px; }

        .ac-card { background: #ffffff; border-radius: 14px; overflow: hidden; border: 1px solid #e5e7eb; box-shadow: 0 1px 3px rgba(0,0,0,0.04); display: flex; flex-direction: column; transition: box-shadow 0.15s ease, transform 0.15s ease; }
        .ac-card:hover { box-shadow: 0 8px 20px rgba(0,0,0,0.08); transform: translateY(-2px); }

        .ac-card-image { position: relative; width: 100%; height: 200px; background: #f3f4f6; }
        .ac-card-image img { width: 100%; height: 100%; object-fit: cover; display: block; }

        .ac-status-badge { position: absolute; top: 10px; right: 10px; font-size: 11px; font-weight: 700; letter-spacing: 0.02em; padding: 4px 10px; border-radius: 999px; text-transform: uppercase; }
        .ac-status-badge.is-active { background: rgba(22,163,74,0.9); color: #ffffff; }
        .ac-status-badge.is-inactive { background: rgba(107,114,128,0.9); color: #ffffff; }

        .ac-card-body { padding: 16px; flex: 1; display: flex; flex-direction: column; }
        .ac-order { font-size: 14px; font-weight: 600; color: #111827; margin: 0; }
        .ac-date { font-size: 12px; color: #9ca3af; margin: 2px 0 0; }
        .ac-item-title { font-size: 14px; color: #374151; font-weight: 600; margin: 10px 0 0; }
        .ac-item-desc { font-size: 12px; color: #6b7280; margin: 4px 0 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

        .ac-card-actions { display: flex; justify-content: space-between; align-items: center; margin-top: auto; padding-top: 14px; border-top: 1px solid #f0f1f3; }
        .ac-move-group, .ac-action-group { display: flex; gap: 6px; }

        .ac-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 8px; border: none; background: #f3f4f6; color: #4b5563; cursor: pointer; transition: all 0.15s ease; }
        .ac-icon-btn:hover { background: #e5e7eb; }
        .ac-icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .ac-toggle.is-on { background: #dcfce7; color: #15803d; }
        .ac-toggle.is-on:hover { background: #bbf7d0; }
        .ac-toggle.is-off { background: #f3f4f6; color: #6b7280; }
        .ac-icon-edit { background: #dbeafe; color: #1d4ed8; }
        .ac-icon-edit:hover { background: #bfdbfe; }
        .ac-icon-delete { background: #fee2e2; color: #dc2626; }
        .ac-icon-delete:hover { background: #fecaca; }

        .ac-edit-form { display: flex; flex-direction: column; gap: 14px; }
        .ac-label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 6px; }
        .ac-input { width: 100%; padding: 9px 12px; font-size: 14px; border: 1px solid #d1d5db; border-radius: 8px; box-sizing: border-box; outline: none; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
        .ac-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }
        .ac-textarea { resize: vertical; }
        .ac-dropzone-sm { padding: 12px; }
        .ac-dropzone { border: 2px dashed #d1d5db; border-radius: 10px; text-align: center; background: #f9fafb; transition: all 0.15s ease; }
        .ac-dropzone:hover { background: #f3f4f6; border-color: #2563eb; }
        .ac-dropzone-label { cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 4px; color: #6b7280; font-size: 12px; }
        .ac-edit-actions { display: flex; gap: 10px; padding-top: 4px; }
      `}</style>
    </AdminLayout>
  );
};

export default AdminCarousel;